import React, { useState } from 'react';

/**
 * Reusable invoice detail component
 * Used by both billingbyscott admin and client apps
 *
 * @param {Object} invoice - The invoice to display
 * @param {Function} onBack - Callback when back button is clicked
 * @param {boolean} showBackButton - Whether to show the back button
 * @param {Function} onPay - Callback to pay the invoice (receives invoiceId), shows Pay button if provided
 * @param {Function} onInvoicePaid - Callback after invoice is successfully paid
 * @param {Function} onCharge - Admin: Callback to charge the invoice (shows Charge button)
 * @param {Function} onVoid - Admin: Callback to void the invoice (shows Void button)
 * @param {boolean} showLineType - Whether to show line_type under description
 * @param {React.ReactNode} headerContent - Custom content to render above the invoice (e.g., breadcrumbs)
 * @param {React.ReactNode} sidebarContent - Custom content to render in a sidebar
 * @param {string} pdfDownloadUrl - URL to download PDF version of invoice (for direct links)
 * @param {Function} onDownloadPdf - Callback to download PDF (for API-authenticated downloads)
 */
export function InvoiceDetail({
  invoice,
  onBack,
  showBackButton = true,
  onPay,
  onInvoicePaid,
  onCharge,
  onVoid,
  showLineType = false,
  headerContent,
  sidebarContent,
  pdfDownloadUrl,
  onDownloadPdf
}) {
  const [paying, setPaying] = useState(false);
  const [charging, setCharging] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async () => {
    if (!onPay) return;

    setPaying(true);
    setError(null);
    try {
      await onPay(invoice.id);
      onInvoicePaid?.();
    } catch (err) {
      setError(err.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const handleCharge = async () => {
    if (!onCharge) return;
    if (!confirm('Charge this invoice using the customer\'s saved payment method?')) return;

    setCharging(true);
    setError(null);
    try {
      await onCharge(invoice.id);
      onInvoicePaid?.();
    } catch (err) {
      setError(err.message || 'Charge failed');
    } finally {
      setCharging(false);
    }
  };

  const handleVoid = async () => {
    if (!onVoid) return;
    if (!confirm('Are you sure you want to void this invoice? This cannot be undone.')) return;

    setVoiding(true);
    setError(null);
    try {
      await onVoid(invoice.id);
    } catch (err) {
      setError(err.message || 'Void failed');
    } finally {
      setVoiding(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!onDownloadPdf) return;

    setDownloading(true);
    setError(null);
    try {
      await onDownloadPdf(invoice.id);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const statusColors = {
    draft: { bg: 'var(--bbs-status-draft-bg, #f3f4f6)', text: 'var(--bbs-status-draft-text, #374151)' },
    open: { bg: 'var(--bbs-status-open-bg, #fef3c7)', text: 'var(--bbs-status-open-text, #92400e)' },
    paid: { bg: 'var(--bbs-status-paid-bg, #dcfce7)', text: 'var(--bbs-status-paid-text, #166534)' },
    void: { bg: 'var(--bbs-status-void-bg, #fee2e2)', text: 'var(--bbs-status-void-text, #991b1b)' },
    uncollectible: { bg: 'var(--bbs-status-void-bg, #fee2e2)', text: 'var(--bbs-status-void-text, #991b1b)' },
  };

  const status = statusColors[invoice.status] || statusColors.draft;

  const mainContent = (
    <div style={{ flex: sidebarContent ? '2' : '1', minWidth: '300px' }}>
      <div style={{ border: '1px solid var(--bbs-border-primary, #e5e7eb)', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'var(--bbs-bg-primary, white)' }}>
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid var(--bbs-border-primary, #e5e7eb)', backgroundColor: 'var(--bbs-table-header-bg, #f9fafb)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--bbs-text-primary, #1f2937)' }}>
                {invoice.number || `Invoice #${invoice.id}`}
              </h3>
              <p style={{ margin: '4px 0 0', color: 'var(--bbs-text-secondary, #6b7280)', fontSize: '14px' }}>
                {formatDate(invoice.issued_at || invoice.created_at)}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: status.bg,
                color: status.text,
              }}>
                {invoice.status}
              </span>

              {/* Client Pay button */}
              {invoice.status === 'open' && onPay && (
                <button
                  onClick={handlePay}
                  disabled={paying}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--bbs-accent-success, #22c55e)',
                    color: 'var(--bbs-text-inverse, white)',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: paying ? 'not-allowed' : 'pointer',
                    opacity: paying ? 0.7 : 1,
                  }}
                >
                  {paying ? 'Processing...' : 'Pay Now'}
                </button>
              )}

              {/* Admin Charge button */}
              {invoice.status === 'open' && onCharge && (
                <button
                  onClick={handleCharge}
                  disabled={charging}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--bbs-accent-primary, #3b82f6)',
                    color: 'var(--bbs-text-inverse, white)',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: charging ? 'not-allowed' : 'pointer',
                    opacity: charging ? 0.7 : 1,
                  }}
                >
                  {charging ? 'Charging...' : 'Charge Now'}
                </button>
              )}

              {/* Admin Void button */}
              {(invoice.status === 'draft' || invoice.status === 'open') && onVoid && (
                <button
                  onClick={handleVoid}
                  disabled={voiding}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--bbs-bg-primary, white)',
                    color: 'var(--bbs-accent-danger, #dc2626)',
                    border: '1px solid var(--bbs-accent-danger-border, #fca5a5)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: voiding ? 'not-allowed' : 'pointer',
                    opacity: voiding ? 0.7 : 1,
                  }}
                >
                  {voiding ? 'Voiding...' : 'Void Invoice'}
                </button>
              )}

              {/* Download PDF button - supports both URL (admin) and callback (client) */}
              {pdfDownloadUrl && (
                <a
                  href={pdfDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--bbs-bg-primary, white)',
                    color: 'var(--bbs-text-primary, #374151)',
                    border: '1px solid var(--bbs-border-secondary, #d1d5db)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    textDecoration: 'none',
                    display: 'inline-block',
                  }}
                >
                  Download PDF
                </a>
              )}
              {onDownloadPdf && !pdfDownloadUrl && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--bbs-bg-primary, white)',
                    color: 'var(--bbs-text-primary, #374151)',
                    border: '1px solid var(--bbs-border-secondary, #d1d5db)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: downloading ? 'not-allowed' : 'pointer',
                    opacity: downloading ? 0.7 : 1,
                  }}
                >
                  {downloading ? 'Downloading...' : 'Download PDF'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div style={{ padding: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--bbs-border-primary, #e5e7eb)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '14px', fontWeight: '500', color: 'var(--bbs-text-secondary, #6b7280)' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '14px', fontWeight: '500', color: 'var(--bbs-text-secondary, #6b7280)' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '14px', fontWeight: '500', color: 'var(--bbs-text-secondary, #6b7280)' }}>Price</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '14px', fontWeight: '500', color: 'var(--bbs-text-secondary, #6b7280)' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items?.map((item, idx) => (
                <tr key={item.id || idx} style={{ borderBottom: '1px solid var(--bbs-border-primary, #e5e7eb)' }}>
                  <td style={{ padding: '12px 0', fontSize: '14px', color: 'var(--bbs-text-primary, #1f2937)' }}>
                    <div>{item.description}</div>
                    {showLineType && item.line_type && (
                      <div style={{ fontSize: '12px', color: 'var(--bbs-text-muted, #9ca3af)' }}>{item.line_type}</div>
                    )}
                  </td>
                  <td style={{ padding: '12px 0', fontSize: '14px', textAlign: 'right', color: 'var(--bbs-text-primary, #1f2937)' }}>{item.quantity}</td>
                  <td style={{ padding: '12px 0', fontSize: '14px', textAlign: 'right', color: 'var(--bbs-text-primary, #1f2937)' }}>{formatPrice(item.unit_price_cents)}</td>
                  <td style={{ padding: '12px 0', fontSize: '14px', textAlign: 'right', fontWeight: '500', color: 'var(--bbs-text-primary, #1f2937)' }}>{formatPrice(item.amount_cents)}</td>
                </tr>
              ))}
              {(!invoice.line_items || invoice.line_items.length === 0) && (
                <tr>
                  <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--bbs-text-secondary, #6b7280)' }}>
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ padding: '12px 0', textAlign: 'right', fontWeight: '500', color: 'var(--bbs-text-primary, #1f2937)' }}>Subtotal</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: '500', color: 'var(--bbs-text-primary, #1f2937)' }}>{formatPrice(invoice.subtotal_cents)}</td>
              </tr>
              {invoice.discount_cents > 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: '8px 0', textAlign: 'right', color: 'var(--bbs-accent-trial, #059669)' }}>Discount</td>
                  <td style={{ padding: '8px 0', textAlign: 'right', color: 'var(--bbs-accent-trial, #059669)' }}>-{formatPrice(invoice.discount_cents)}</td>
                </tr>
              )}
              <tr style={{ borderTop: '2px solid var(--bbs-border-primary, #e5e7eb)' }}>
                <td colSpan={3} style={{ padding: '12px 0', textAlign: 'right', fontSize: '16px', fontWeight: '600', color: 'var(--bbs-text-primary, #1f2937)' }}>Total</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontSize: '16px', fontWeight: '600', color: 'var(--bbs-text-primary, #1f2937)' }}>{formatPrice(invoice.total_cents)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer info */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--bbs-border-primary, #e5e7eb)', backgroundColor: 'var(--bbs-table-header-bg, #f9fafb)', fontSize: '14px', color: 'var(--bbs-text-secondary, #6b7280)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span>Period: {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}</span>
            {invoice.due_date && <span>Due: {formatDate(invoice.due_date)}</span>}
            {invoice.paid_at && <span>Paid: {formatDate(invoice.paid_at)}</span>}
            {invoice.voided_at && <span>Voided: {formatDate(invoice.voided_at)}</span>}
            {invoice.square_payment_id && (
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                Payment: {invoice.square_payment_id}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Custom header content (e.g., breadcrumbs) */}
      {headerContent}

      {/* Back button */}
      {showBackButton && onBack && (
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 0',
            marginBottom: '16px',
            background: 'none',
            border: 'none',
            color: 'var(--bbs-accent-primary, #3b82f6)',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          &larr; Back to invoices
        </button>
      )}

      {/* Error message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          backgroundColor: 'var(--bbs-error-bg, #fef2f2)',
          border: '1px solid var(--bbs-error-border, #fecaca)',
          borderRadius: '6px',
          color: 'var(--bbs-error-text, #991b1b)',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {/* Main content with optional sidebar */}
      {sidebarContent ? (
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {mainContent}
          <div style={{ flex: '1', minWidth: '250px' }}>
            {sidebarContent}
          </div>
        </div>
      ) : (
        mainContent
      )}
    </div>
  );
}

export default InvoiceDetail;
